
import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, CheckCircle, Calendar, Phone, FileText, Search, ArrowUpDown, ArrowUp, ArrowDown, AlertCircle, Users, KeyRound } from "lucide-react";
import { format } from "date-fns";
import { parseArrayField, safeFormatDate } from "@/lib/safeParse";

export default function ManageConsequences() {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active"); // "active" | "resolved"
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [message, setMessage] = useState(null); // Added message state
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const isMainAdmin = user?.role === 'admin';
  const isEAStaff = user?.role === 'user' && user?.user_type === 'ea_staff';
  const isPrincipal = user?.role === 'user' && user?.user_type === 'principal';
  const hasReadAccess = isMainAdmin || isEAStaff || isPrincipal;

  const { data: trackingRecords = [], isLoading } = useQuery({
    queryKey: ['eaMissTracking'],
    queryFn: () => base44.entities.EAMissTracking.list('-created_date'),
  });

  const { data: closeouts = [] } = useQuery({
    queryKey: ['closeouts'],
    queryFn: () => base44.entities.Closeout.list('-closeout_date')
  });

  const { data: consequenceLevels = [] } = useQuery({
    queryKey: ['consequenceLevels'],
    queryFn: () => base44.entities.ConsequenceLevel.list('sort_order'),
  });

  const { data: aps = [] } = useQuery({
    queryKey: ['aps'],
    queryFn: async () => {
      const allUsers = await base44.entities.User.list();
      return allUsers.filter(u => u.role === 'user' && u.user_type === 'assistant_principal');
    },
  });

  const { data: students = [] } = useQuery({
    queryKey: ['students'],
    queryFn: () => base44.entities.Student.list(),
  });

  const syncMissesMutation = useMutation({
    mutationFn: async () => {
      // Get consequence level settings
      const levels = consequenceLevels.length > 0 ? consequenceLevels : [
        { level_key: 'warning', refusal_threshold: 1, level_name: 'Warning', badge_emoji: '⚠️', badge_color: 'bg-green-100 text-green-800', level_description: 'Conference with student, document + 1 makeup day' },
        { level_key: 'parent_contact', refusal_threshold: 2, level_name: 'Parent Contact', badge_emoji: '📞', badge_color: 'bg-yellow-100 text-yellow-800', level_description: 'Phone call to parent/guardian + 2 makeup days' },
        { level_key: 'detention', refusal_threshold: 3, level_name: 'Detention', badge_emoji: '📋', badge_color: 'bg-orange-100 text-orange-800', level_description: 'Assign detention, notify parent + 3 makeup days' },
        { level_key: 'iss', refusal_threshold: 4, level_name: 'ISS', badge_emoji: '🚫', badge_color: 'bg-red-100 text-red-800', level_description: 'In-School Suspension recommendation + 4 makeup days' },
        { level_key: 'admin_review', refusal_threshold: 5, level_name: 'Admin Review', badge_emoji: '⛔', badge_color: 'bg-red-900 text-white', level_description: 'Escalate to admin, OSS consideration + 5+ makeup days' }
      ];

      // Fetch all students to look up names
      const allStudents = await base44.entities.Student.list();

      // Get all closeout snapshots and count REFUSALS BY UNIQUE DATES (not assignments)
      const studentMissMap = new Map();

      // Normalize student_id everywhere in this mutation so leading-zero
      // variants ("093673" vs "93673") collapse to a single canonical key.
      const STUDENT_ID_PAD = 6;
      const normalizeSid = (raw) => {
        const s = String(raw ?? '').trim();
        if (!s) return '';
        return /^\d+$/.test(s) ? s.padStart(STUDENT_ID_PAD, '0') : s;
      };

      for (const closeout of closeouts) {
        if (!closeout.roster_snapshot) continue;

        try {
          const snapshot = JSON.parse(closeout.roster_snapshot);
          const eaRoster = snapshot.ea_roster || [];

          // Count ONLY students marked as REFUSAL, group by UNIQUE DATES
          eaRoster.forEach((entry) => {
            if (entry.attendance_status === 'refusal') {
              const key = normalizeSid(entry.other_id);
              if (!key) return;
              if (!studentMissMap.has(key)) {
                // Look up student in Student database, matching on normalized id
                const student = allStudents.find(
                  (s) => normalizeSid(s.student_id) === key
                );

                studentMissMap.set(key, {
                  student_id: key,
                  student_first_name: student?.first_name || entry.student_first_name || '',
                  student_last_name: student?.last_name || entry.student_last_name || '',
                  grade_level: student?.grade_level || entry.grade_level,
                  uniqueDates: new Set() // Track unique dates
                });
              }
              const data = studentMissMap.get(key);
              // Add to unique dates set (automatically deduplicates same day)
              data.uniqueDates.add(closeout.closeout_date);
            }
          });
        } catch (err) {
          console.error('Error parsing snapshot:', err);
        }
      }

      // Update or create tracking records
      let updated = 0;
      let created = 0;
      let apAssigned = 0;
      let duplicatesMerged = 0;

      // Work with a mutable local copy of the tracker list so consolidation
      // can rebuild it without touching the react-query cache.
      let records = [...trackingRecords];

      // -------------------------------------------------------------------
      // CONSOLIDATE DUPLICATES FIRST.
      // A student should only ever have ONE active consequence record. If
      // duplicates exist, keep the most-progressed record (highest
      // miss_count, then most-severe current_consequence) and carry every
      // shred of history from the losers onto it before deleting them.
      // -------------------------------------------------------------------
      const CONSEQUENCE_RANK = {
        warning: 1,
        parent_contact: 2,
        detention: 3,
        iss: 4,
        admin_review: 5,
      };
      // (normalizeSid was hoisted above the studentMissMap loop.)
      const byStudent = new Map();
      for (const rec of records) {
        const k = normalizeSid(rec.student_id);
        if (!k) continue;
        if (!byStudent.has(k)) byStudent.set(k, []);
        byStudent.get(k).push(rec);
      }
      const parseJsonArr = (v) => {
        if (!v) return [];
        if (Array.isArray(v)) return v;
        try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
      };
      const uniqSort = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();

      // We'll rebuild trackingRecords in memory as we consolidate so the
      // rest of the sync loop sees a clean 1-record-per-student view.
      const consolidated = [];
      for (const [studentId, group] of byStudent.entries()) {
        if (group.length === 1) {
          consolidated.push(group[0]);
          continue;
        }
        // Pick the most progressed record as the "winner"
        group.sort((a, b) => {
          const mA = parseInt(a.miss_count || 0);
          const mB = parseInt(b.miss_count || 0);
          if (mA !== mB) return mB - mA;
          const rA = CONSEQUENCE_RANK[a.current_consequence] || 0;
          const rB = CONSEQUENCE_RANK[b.current_consequence] || 0;
          if (rA !== rB) return rB - rA;
          return (b.updated_date || '').localeCompare(a.updated_date || '');
        });
        const winner = group[0];
        const losers = group.slice(1);

        // Merge every history field: union arrays, keep max counts, prefer
        // non-empty scalar fields from any record.
        const merged = { ...winner };
        merged.miss_history = uniqSort([
          ...parseJsonArr(winner.miss_history),
          ...losers.flatMap((l) => parseJsonArr(l.miss_history)),
        ]);
        merged.miss_count = Math.max(
          parseInt(winner.miss_count || 0),
          ...losers.map((l) => parseInt(l.miss_count || 0)),
          merged.miss_history.length
        );
        merged.makeup_days_owed = Math.max(
          parseInt(winner.makeup_days_owed || 0),
          ...losers.map((l) => parseInt(l.makeup_days_owed || 0))
        );
        merged.makeup_days_completed = Math.max(
          parseInt(winner.makeup_days_completed || 0),
          ...losers.map((l) => parseInt(l.makeup_days_completed || 0))
        );
        merged.makeup_dates = uniqSort([
          ...parseJsonArr(winner.makeup_dates),
          ...losers.flatMap((l) => parseJsonArr(l.makeup_dates)),
        ]);
        merged.scheduled_makeup_dates = uniqSort([
          ...parseJsonArr(winner.scheduled_makeup_dates),
          ...losers.flatMap((l) => parseJsonArr(l.scheduled_makeup_dates)),
        ]);
        merged.refusal_history = uniqSort([
          ...parseJsonArr(winner.refusal_history),
          ...losers.flatMap((l) => parseJsonArr(l.refusal_history)),
        ]);
        // Parent contact history: winner takes precedence, but append
        // anything from losers not already present.
        const winParent = parseJsonArr(winner.parent_contact_history);
        const dedupeKey = (o) =>
          `${o?.date || o?.contacted_at || ''}|${o?.notes || ''}|${o?.method || ''}`;
        const seen = new Set(winParent.map(dedupeKey));
        const extraParent = losers
          .flatMap((l) => parseJsonArr(l.parent_contact_history))
          .filter((o) => {
            const k = dedupeKey(o);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });
        merged.parent_contact_history = [...winParent, ...extraParent];
        // Scalar carry-forward: keep truthy from winner, else fall back
        // to any loser that has a value. Notes are concatenated.
        const pickTruthy = (field) =>
          winner[field] || losers.map((l) => l[field]).find((v) => v) || winner[field];
        merged.principal_informed =
          winner.principal_informed || losers.some((l) => l.principal_informed);
        merged.parent_contacted =
          winner.parent_contacted || losers.some((l) => l.parent_contacted);
        merged.assigned_ap_email = pickTruthy('assigned_ap_email');
        merged.assigned_ap_name = pickTruthy('assigned_ap_name');
        merged.grade_level = pickTruthy('grade_level');
        // Concatenate notes so nothing is lost.
        const allNotes = [winner.notes, ...losers.map((l) => l.notes)]
          .filter((n) => n && n.trim())
          .filter((n, i, arr) => arr.indexOf(n) === i);
        merged.notes = allNotes.join('\n---\n') || winner.notes || null;
        merged.last_miss_date =
          [winner.last_miss_date, ...losers.map((l) => l.last_miss_date)]
            .filter(Boolean)
            .sort()
            .slice(-1)[0] || winner.last_miss_date;

        // Recompute current_consequence from the merged miss_count so it
        // reflects the actual most-progressed level.
        const sortedForRank = [...levels].sort((a, b) => b.refusal_threshold - a.refusal_threshold);
        let mergedConsequence = winner.current_consequence || 'warning';
        for (const level of sortedForRank) {
          if ((merged.miss_count || 0) >= level.refusal_threshold) {
            mergedConsequence = level.level_key;
            break;
          }
        }
        // Only bump forward — never demote a manually-set level.
        if (
          (CONSEQUENCE_RANK[mergedConsequence] || 0) >
          (CONSEQUENCE_RANK[winner.current_consequence] || 0)
        ) {
          merged.current_consequence = mergedConsequence;
        }

        // Persist the merged winner
        await base44.entities.EAMissTracking.update(winner.id, {
          miss_count: merged.miss_count,
          miss_history: JSON.stringify(merged.miss_history),
          makeup_days_owed: merged.makeup_days_owed,
          makeup_days_completed: merged.makeup_days_completed,
          makeup_dates: JSON.stringify(merged.makeup_dates),
          scheduled_makeup_dates: JSON.stringify(merged.scheduled_makeup_dates),
          refusal_history: JSON.stringify(merged.refusal_history),
          parent_contact_history: JSON.stringify(merged.parent_contact_history),
          principal_informed: merged.principal_informed,
          parent_contacted: merged.parent_contacted,
          current_consequence: merged.current_consequence,
          notes: merged.notes,
          last_miss_date: merged.last_miss_date,
          assigned_ap_email: merged.assigned_ap_email,
          assigned_ap_name: merged.assigned_ap_name,
          grade_level: merged.grade_level,
        });
        // Delete the losers
        for (const l of losers) {
          await base44.entities.EAMissTracking.delete(l.id);
          duplicatesMerged++;
        }
        consolidated.push(merged);
      }
      // Overwrite the working list so the rest of the sync loop finds only
      // the consolidated single record per student.
      records = consolidated;

      for (const [studentId, data] of studentMissMap.entries()) {
        // Count unique dates (not assignments)
        const missCount = data.uniqueDates.size;

        if (missCount === 0) continue;

        // Determine consequence level based on dynamic settings
        let consequence = 'warning';
        const sortedLevels = [...levels].sort((a, b) => b.refusal_threshold - a.refusal_threshold);

        for (const level of sortedLevels) {
          if (missCount >= level.refusal_threshold) {
            consequence = level.level_key;
            break;
          }
        }

        // AUTO-ASSIGN AP based on student grade + AP cohort setting
        let assignedAP = null;
        const grade = parseInt(data.grade_level);
        if (grade >= 4 && grade <= 5) {
          assignedAP = aps.find((ap) => ap.ap_grade_cohort === '4-5');
        } else if (grade >= 6 && grade <= 8) {
          assignedAP = aps.find((ap) => ap.ap_grade_cohort === '6-8');
        }

        // Get unique dates as sorted array
        const uniqueDatesArray = Array.from(data.uniqueDates).sort();

        // Check if record exists (consolidated) — always look at the local
        // `records` list using the NORMALIZED student_id.
        const normSid = normalizeSid(studentId);
        const existing = records.find((m) => normalizeSid(m.student_id) === normSid);

        if (existing) {
          // Update existing record. NEVER demote a manually-set level —
          // only bump the consequence forward. Every other history field
          // (notes, parent_contact_history, principal_informed, refusal_history)
          // is left untouched because the PATCH only sets the listed keys.
          const currRank = CONSEQUENCE_RANK[existing.current_consequence] || 0;
          const newRank = CONSEQUENCE_RANK[consequence] || 0;
          const nextConsequence = newRank > currRank ? consequence : existing.current_consequence;
          await base44.entities.EAMissTracking.update(existing.id, {
            miss_count: missCount,
            current_consequence: nextConsequence,
            last_miss_date: uniqueDatesArray[uniqueDatesArray.length - 1],
            miss_history: JSON.stringify(uniqueDatesArray),
            makeup_days_owed: Math.max(missCount, parseInt(existing.makeup_days_owed || 0)),
            assigned_ap_email: assignedAP?.email || existing.assigned_ap_email,
            assigned_ap_name: assignedAP?.full_name || existing.assigned_ap_name,
            student_first_name: data.student_first_name,
            student_last_name: data.student_last_name,
          });
          if (assignedAP && existing.assigned_ap_email !== assignedAP.email) {
            apAssigned++;
          }
          updated++;
        } else {
          // Create new record
          await base44.entities.EAMissTracking.create({
            student_id: studentId,
            student_first_name: data.student_first_name,
            student_last_name: data.student_last_name,
            // student_name was removed based on outline
            grade_level: data.grade_level,
            miss_count: missCount,
            current_consequence: consequence,
            last_miss_date: uniqueDatesArray[uniqueDatesArray.length - 1],
            miss_history: JSON.stringify(uniqueDatesArray),
            makeup_days_owed: missCount,
            makeup_days_completed: 0,
            makeup_dates: JSON.stringify([]),
            scheduled_makeup_dates: JSON.stringify([]),
            assigned_ap_email: assignedAP?.email || null,
            assigned_ap_name: assignedAP?.full_name || null,
            status: 'active'
          });
          if (assignedAP) {
            apAssigned++;
          }
          created++;
        }
      }

      return { updated, created, total: studentMissMap.size, apAssigned, duplicatesMerged };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['eaMissTracking']);
      const mergedMsg = data.duplicatesMerged > 0
        ? ` Consolidated ${data.duplicatesMerged} duplicate record(s).`
        : '';
      setMessage({ type: 'success', text: `Sync complete: Updated ${data.updated}, Created ${data.created} records.${mergedMsg} ${data.apAssigned > 0 ? `Assigned ${data.apAssigned} APs.` : ''}` });
    },
    onError: (err) => {
      console.error('Failed to sync EA misses', err);
      setMessage({ type: 'error', text: `Failed to sync EA misses: ${err.message}` });
    }
  });

  const fixNamesMutation = useMutation({
    mutationFn: async () => {
      const allStudents = await base44.entities.Student.list();
      let fixed = 0;
      let notFound = 0;

      for (const record of trackingRecords) {
        // Look up student in database
        const student = allStudents.find(s => s.student_id === record.student_id);
        
        if (student) {
          await base44.entities.EAMissTracking.update(record.id, {
            student_first_name: student.first_name,
            student_last_name: student.last_name,
            grade_level: student.grade_level || record.grade_level
          });
          fixed++;
        } else {
          notFound++;
        }
      }

      return { fixed, notFound };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setMessage({ 
        type: 'success', 
        text: `✅ Fixed ${data.fixed} student names! ${data.notFound > 0 ? `⚠️ ${data.notFound} students not found in database.` : ''}` 
      });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to fix names: ${err.message}` });
    }
  });

  const reassignAPsMutation = useMutation({
    mutationFn: async () => {
      let reassigned = 0;
      let noAP = 0;

      for (const track of trackingRecords) {
        const grade = parseInt(track.grade_level);
        let assignedAP = null;

        // Find AP based on grade + cohort
        if (grade >= 4 && grade <= 5) {
          assignedAP = aps.find((ap) => ap.ap_grade_cohort === '4-5');
        } else if (grade >= 6 && grade <= 8) {
          assignedAP = aps.find((ap) => ap.ap_grade_cohort === '6-8');
        }

        // Only update if AP assignment changes or was previously null
        if (assignedAP && (track.assigned_ap_email !== assignedAP.email || track.assigned_ap_email === null)) {
          await base44.entities.EAMissTracking.update(track.id, {
            assigned_ap_email: assignedAP.email,
            assigned_ap_name: assignedAP.full_name
          });
          reassigned++;
        } else if (!assignedAP && track.status !== 'resolved') { // Count only active/escalated records without AP
          noAP++;
        }
      }

      return { reassigned, noAP };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setMessage({ type: 'success', text: `Re-assigned APs for ${data.reassigned} students. ${data.noAP > 0 ? `${data.noAP} students still without AP.` : ''}` });
    },
    onError: (err) => {
      console.error('Failed to re-assign APs', err);
      setMessage({ type: 'error', text: `Failed to re-assign APs: ${err.message}` });
    }
  });

  // Canonicalize Tracker Keys — one-click server-side repair that re-keys
  // every ea_miss_trackings row to its canonical students.student_id. Replaces
  // the old Fix Names + Sync EA Misses + Re-assign APs button trio.
  const canonicalizeTrackerKeysMutation = useMutation({
    mutationFn: async () => {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/closeout/canonicalize-tracker-keys`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["eaMissTracking"]);
      const s = data?.stats || {};
      setMessage({
        type: "success",
        text: `Canonicalize complete: re-keyed ${s.rekeyed ?? 0}, unchanged ${s.unchanged ?? 0}${
          s.resolved_jayleen ? `, resolved Jayleen ${s.resolved_jayleen}` : ""
        }.`,
      });
    },
    onError: (err) => {
      setMessage({ type: "error", text: err.message || "Canonicalize failed" });
    },
  });


  const notifyAPsMutation = useMutation({
    mutationFn: async () => {
      // Get email template settings
      const allSettings = await base44.entities.Settings.list();
      const emailTemplate = allSettings.find(s => s.setting_key === 'ea_notification_email_template'); // Use a specific key for this template
      
      // Default template if none exists
      const subjectTemplate = emailTemplate?.email_subject || '⚠️ EA Consequences - Students Requiring Intervention';
      const bodyTemplate = emailTemplate?.email_body || `Dear {ap_name},

You have {student_count} student{student_plural} in your grade cohort who require intervention for refusing to attend Eagle Academy.

STUDENTS REQUIRING ATTENTION:
{student_list}

NEXT STEPS:
1. Log into the EA Management System
2. Navigate to "EA Consequences" in the menu
3. Review each student's refusal history
4. Document interventions taken
5. Track makeup day completion
6. Update parent contact information as needed

Please address these cases as soon as possible. Each refusal day requires a makeup day, and consequences escalate based on the number of refusals.

If you have questions or need support, please contact the EA Coordinator.

Thank you for your prompt attention to these matters.

Eagle Academy Management System
This is an automated notification.`;

      // Group students by AP
      const apGroups = new Map();

      trackingRecords.filter((m) => m.status === 'active' && m.assigned_ap_email).forEach((student) => {
        if (!apGroups.has(student.assigned_ap_email)) {
          apGroups.set(student.assigned_ap_email, {
            ap_name: student.assigned_ap_name,
            students: []
          });
        }
        apGroups.get(student.assigned_ap_email).students.push(student);
      });

      // Send email to each AP
      for (const [email, data] of apGroups.entries()) {
        const studentList = data.students.map((s) => {
          const makeupRemaining = (s.makeup_days_owed || 0) - (s.makeup_days_completed || 0);
          const fullName = `${s.student_first_name || ''} ${s.student_last_name || ''}`.trim() || 'Unknown Student';
          return `• ${fullName} (Grade ${s.grade_level})
   - ${s.miss_count} EA refusal day${s.miss_count > 1 ? 's' : ''}
   - Consequence Level: ${getConsequenceLabel(s.current_consequence)}
   - Makeup Days Remaining: ${makeupRemaining}
   - Last Refusal: ${s.last_miss_date || 'N/A'}`;
        }).join('\n\n');

        // Replace placeholders
        const subject = subjectTemplate
          .replace(/{ap_name}/g, data.ap_name)
          .replace(/{student_count}/g, data.students.length.toString())
          .replace(/{student_plural}/g, data.students.length > 1 ? 's' : '');

        const body = bodyTemplate
          .replace(/{ap_name}/g, data.ap_name)
          .replace(/{student_count}/g, data.students.length.toString())
          .replace(/{student_plural}/g, data.students.length > 1 ? 's' : '')
          .replace(/{student_list}/g, studentList);

        await base44.integrations.Core.SendEmail({
          to: email,
          subject: subject,
          body: body
        });
      }

      return apGroups.size;
    },
    onSuccess: (count) => {
      setMessage({ type: 'success', text: `Email notifications sent to ${count} APs.` });
    },
    onError: (err) => {
      console.error('Failed to send notifications', err);
      setMessage({ type: 'error', text: `Failed to send notifications: ${err.message}` });
    }
  });

  const bulkMarkCompletedMutation = useMutation({
    mutationFn: async (selectedStudentIds) => { // Modified to accept selectedStudentIds directly
      let updated = 0;
      const today = format(new Date(), 'yyyy-MM-dd');

      for (const studentId of selectedStudentIds) {
        const student = trackingRecords.find((m) => m.id === studentId);
        if (!student) continue;

        const remaining = (student.makeup_days_owed || 0) - (student.makeup_days_completed || 0);
        if (remaining <= 0) continue; // Skip if no days owed

        // Add one makeup day
        const newCompleted = (student.makeup_days_completed || 0) + 1;
        let makeupDates = [];
        try {
          makeupDates = student.makeup_dates ? JSON.parse(student.makeup_dates) : [];
        } catch (e) {
          makeupDates = [];
        }
        makeupDates.push(today);

        // Auto-resolve if all makeup days completed
        const newStatus = newCompleted >= (student.makeup_days_owed || 0) ? 'resolved' : student.status;

        await base44.entities.EAMissTracking.update(studentId, {
          makeup_days_completed: newCompleted,
          makeup_dates: JSON.stringify(makeupDates),
          status: newStatus
        });
        updated++;
      }

      return updated;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setMessage({ type: 'success', text: `Successfully marked ${count} makeup days as completed.` });
    },
    onError: (err) => {
      console.error('Failed to bulk update', err);
      setMessage({ type: 'error', text: `Failed to bulk mark completed: ${err.message}` });
    }
  });

  const downloadMakeupReport = () => {
    const headers = [
      'Date Added', 'Student Last Name', 'Student First Name', 'Student ID', 'Grade', 'Refusal Days',
      'Makeup Days Owed', 'Makeup Days Completed', 'Makeup Days Remaining',
      'Scheduled Makeup Dates', 'Completed Makeup Dates', 'Consequence Level',
      'Assigned AP', 'Status', 'Last Refusal Date'
    ];


    const rows = trackingRecords.map((track) => {
      const remaining = (track.makeup_days_owed || 0) - (track.makeup_days_completed || 0);
      const scheduledDates = track.scheduled_makeup_dates ? JSON.parse(track.scheduled_makeup_dates).join(', ') : '';
      const completedDates = track.makeup_dates ? JSON.parse(track.makeup_dates).join(', ') : '';

      return [
        track.created_date ? format(new Date(track.created_date), 'yyyy-MM-dd') : '',
        track.student_last_name || '',
        track.student_first_name || '',
        track.student_id,
        track.grade_level,
        track.miss_count || 0,
        track.makeup_days_owed || 0,
        track.makeup_days_completed || 0,
        remaining,
        scheduledDates,
        completedDates,
        getConsequenceLabel(track.current_consequence),
        track.assigned_ap_name || '',
        track.status,
        track.last_miss_date || ''
      ];

    });

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EA_Makeup_Days_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const getConsequenceLabel = (consequence) => {
    const level = consequenceLevels.find((l) => l.level_key === consequence);
    if (level) {
      return level.level_name;
    }

    const labels = {
      warning: 'Warning/Conference',
      parent_contact: 'Parent Contact',
      detention: 'Detention',
      iss: 'ISS',
      admin_review: 'Admin Review'
    };
    return labels[consequence] || consequence;
  };

  // Split records by tab BEFORE the search/status filter so the tab counts
  // reflect the true pool of records, not the currently-filtered subset.
  const tabActiveRecords = trackingRecords.filter((r) => r.status !== 'resolved');
  const tabResolvedRecords = trackingRecords.filter((r) => r.status === 'resolved');
  const tabPool = activeTab === 'resolved' ? tabResolvedRecords : tabActiveRecords;

  const filteredRecords = tabPool.filter(record => {
    const fullName = `${record.student_first_name || ''} ${record.student_last_name || ''}`.trim().toLowerCase();
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      record.student_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.student_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.student_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.assigned_ap_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || record.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Default multi-key sort order for the student table:
  //   1. AP cohort (4-5 before 6-8) — derived from student's grade_level
  //   2. Consequence level (1st -> 2nd -> 3rd escalation)
  //   3. Last name A-Z
  // Applies to both Admin and AP views whenever the user hasn't explicitly
  // clicked a column header (sortConfig.key === null).
  const cohortOrder = (record) => {
    const g = parseInt(record.grade_level, 10);
    if (g === 4 || g === 5) return 0; // 4-5 cohort first
    if (g >= 6 && g <= 8) return 1;   // 6-8 cohort next
    return 2;                          // anything else last
  };
  const consequenceOrder = (record) => {
    const order = ['warning', 'parent_contact', 'detention', 'iss', 'admin_review'];
    const idx = order.indexOf(record.current_consequence);
    return idx === -1 ? order.length : idx;
  };

  const sortedRecords = [...filteredRecords].sort((a, b) => {
    if (!sortConfig.key) {
      const c = cohortOrder(a) - cohortOrder(b);
      if (c !== 0) return c;
      const q = consequenceOrder(a) - consequenceOrder(b);
      if (q !== 0) return q;
      const la = (a.student_last_name || '').toLowerCase();
      const lb = (b.student_last_name || '').toLowerCase();
      if (la < lb) return -1;
      if (la > lb) return 1;
      return 0;
    }

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // For numeric fields
    if (sortConfig.key === 'miss_count' || sortConfig.key === 'makeup_days_owed' || sortConfig.key === 'makeup_days_completed' || sortConfig.key === 'grade_level') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else if (typeof aValue === 'string') {
      // Convert to lowercase for string comparison
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="w-4 h-4 ml-1 text-blue-600" /> :
      <ArrowDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  const addNoteMutation = useMutation({
    mutationFn: async ({ recordId, note }) => {
      const record = trackingRecords.find(r => r.id === recordId);
      const notes = parseArrayField(record?.intervention_notes);

      const newNoteEntry = {
        date: format(new Date(), 'yyyy-MM-dd'),
        note: note,
        timestamp: new Date().toISOString()
      };

      notes.push(newNoteEntry);

      await base44.entities.EAMissTracking.update(recordId, {
        intervention_notes: JSON.stringify(notes)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setNewNote("");
      setMessage({ type: 'success', text: "Note added successfully!" });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to add note: ${err.message}` });
    }
  });

  const resolveMutation = useMutation({
    mutationFn: async (recordId) => {
      await base44.entities.EAMissTracking.update(recordId, {
        status: 'resolved'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setShowDetailsModal(false);
      setSelectedStudent(null);
      setMessage({ type: 'success', text: "Case marked as resolved!" });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to resolve case: ${err.message}` });
    }
  });

  const escalateMutation = useMutation({
    mutationFn: async (recordId) => {
      const record = trackingRecords.find(r => r.id === recordId);
      const principalHistory = parseArrayField(record?.principal_contact_history);

      principalHistory.push(format(new Date(), 'yyyy-MM-dd'));

      await base44.entities.EAMissTracking.update(recordId, {
        status: 'escalated',
        principal_contact_history: JSON.stringify(principalHistory)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setShowDetailsModal(false);
      setSelectedStudent(null);
      setMessage({ type: 'success', text: "Case escalated to principal!" });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to escalate case: ${err.message}` });
    }
  });

  const deleteRecordMutation = useMutation({
    mutationFn: async (recordId) => {
      return base44.entities.EAMissTracking.delete(recordId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setShowDetailsModal(false);
      setSelectedStudent(null);
      setMessage({ type: 'success', text: "Record permanently deleted!" });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to delete record: ${err.message}` });
    }
  });

  const assignAPMutation = useMutation({
    mutationFn: async ({ recordId, apEmail, apName }) => {
      await base44.entities.EAMissTracking.update(recordId, {
        assigned_ap_email: apEmail,
        assigned_ap_name: apName
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setMessage({ type: 'success', text: "AP assigned successfully!" });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to assign AP: ${err.message}` });
    }
  });

  const getConsequenceDisplay = (consequence) => {
    const level = consequenceLevels.find(l => l.level_key === consequence);
    if (level) {
      return {
        color: level.badge_color,
        label: `${level.badge_emoji} ${level.level_name}`
      };
    }

    const badges = {
      warning: { color: 'bg-green-100 text-green-800', label: '⚠️ Warning' },
      parent_contact: { color: 'bg-yellow-100 text-yellow-800', label: '📞 Parent Contact' },
      detention: { color: 'bg-orange-100 text-orange-800', label: '📋 Detention' },
      iss: { color: 'bg-red-100 text-red-800', label: '🚫 ISS' },
      admin_review: { color: 'bg-red-900 text-white', label: '⛔ Admin Review' }
    };
    return badges[consequence] || { color: 'bg-slate-100 text-slate-800', label: consequence };
  };

  const getAPForGrade = (gradeLevel) => {
    const grade = parseInt(gradeLevel);
    if (grade >= 4 && grade <= 5) {
      return aps.find(ap => ap.ap_grade_cohort === '4-5');
    } else if (grade >= 6 && grade <= 8) {
      return aps.find(ap => ap.ap_grade_cohort === '6-8');
    }
    return null;
  };

  const handleViewDetails = (record) => {
    setSelectedStudent(record);
    setShowDetailsModal(true);
  };

  const handleAddNote = (recordId) => {
    if (!newNote.trim()) return;
    addNoteMutation.mutate({ recordId, note: newNote });
  };

  const handleResolve = (recordId) => {
    if (confirm("Mark this case as resolved? This will archive the record.")) {
      resolveMutation.mutate(recordId);
    }
  };

  const handleEscalate = (recordId) => {
    if (confirm("Escalate to principal? This will mark the case as escalated and log principal contact.")) {
      escalateMutation.mutate(recordId);
    }
  };

  const handleAssignAP = (recordId, gradeLevel) => {
    const ap = getAPForGrade(gradeLevel);
    if (ap) {
      assignAPMutation.mutate({
        recordId,
        apEmail: ap.email,
        apName: ap.full_name
      });
    } else {
      alert("No AP found for this grade level. Please configure APs in User Management.");
    }
  };

  if (!hasReadAccess) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const activeCount = trackingRecords.filter(r => r.status === 'active').length;
  const resolvedCount = trackingRecords.filter(r => r.status === 'resolved').length;
  const escalatedCount = trackingRecords.filter(r => r.status === 'escalated').length;

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-full mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">EA Consequences</h1>
          <p className="text-slate-600 mt-1">Track and Manage EA Refusals and Interventions</p>
        </div>

        {isEAStaff && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>Read-Only Mode:</strong> You can view and search consequences to help answer student questions, but cannot make changes.
            </AlertDescription>
          </Alert>
        )}

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {isMainAdmin && (
          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
            <CardHeader>
              <CardTitle className="text-blue-900">Admin Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => {
                    if (confirm('Canonicalize tracker keys? This re-keys every EA consequence record to the student’s canonical ID from the students collection. Safe to re-run.')) {
                      canonicalizeTrackerKeysMutation.mutate();
                    }
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 h-auto py-4 flex flex-col items-start gap-2"
                  disabled={canonicalizeTrackerKeysMutation.isPending}
                  data-testid="canonicalize-tracker-keys-btn"
                >
                  <KeyRound className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-semibold">Canonicalize Tracker Keys</div>
                    <div className="text-xs font-normal opacity-90">Re-key trackers to canonical student IDs</div>
                  </div>
                  {canonicalizeTrackerKeysMutation.isPending && <div className="text-xs">Processing...</div>}
                  {canonicalizeTrackerKeysMutation.isSuccess && (
                    <div className="text-xs">
                      ✓ Re-keyed {canonicalizeTrackerKeysMutation.data?.stats?.rekeyed ?? 0}
                    </div>
                  )}
                </Button>

                <Button
                  onClick={() => {
                    if (confirm(`Send email notifications to all APs about their assigned students with active consequences?`)) {
                      notifyAPsMutation.mutate();
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 h-auto py-4 flex flex-col items-start gap-2"
                  disabled={notifyAPsMutation.isPending}
                >
                  <FileText className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-semibold">Notify APs</div>
                    <div className="text-xs font-normal opacity-90">Send email notifications</div>
                  </div>
                  {notifyAPsMutation.isPending && <div className="text-xs">Sending...</div>}
                  {notifyAPsMutation.isSuccess && (
                    <div className="text-xs">✓ Sent to {notifyAPsMutation.data} APs</div>
                  )}
                </Button>

                <Button
                  onClick={downloadMakeupReport}
                  variant="outline"
                  className="border-slate-300 h-auto py-4 flex flex-col items-start gap-2"
                >
                  <FileText className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-semibold">Download Report</div>
                    <div className="text-xs font-normal">Export makeup days CSV</div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-red-700 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Active Cases</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{activeCount}</div>
              <p className="text-sm opacity-75 mt-2">Needs intervention</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Resolved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{resolvedCount}</div>
              <p className="text-sm opacity-75 mt-2">Issue fixed</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-800 to-red-900 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Escalated</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{escalatedCount}</div>
              <p className="text-sm opacity-75 mt-2">Sent to admin</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-none shadow-lg">
          <CardHeader>
            {/* Tabs: Active Cases vs Resolved. Counts reflect pool BEFORE
                search/status filter so users know the real totals. */}
            <div className="flex gap-2 border-b border-slate-200 mb-4">
              <button
                type="button"
                data-testid="tab-active-cases"
                onClick={() => setActiveTab('active')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === 'active'
                    ? 'border-red-600 text-red-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Active Cases ({tabActiveRecords.length})
              </button>
              <button
                type="button"
                data-testid="tab-resolved"
                onClick={() => setActiveTab('resolved')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === 'resolved'
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Resolved ({tabResolvedRecords.length})
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by student name, ID, or AP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="escalated">Escalated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_last_name')}
                    >
                      <div className="flex items-center">
                        Last Name
                        {getSortIcon('student_last_name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_first_name')}
                    >
                      <div className="flex items-center">
                        First Name
                        {getSortIcon('student_first_name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('grade_level')}
                    >
                      <div className="flex items-center">
                        Grade
                        {getSortIcon('grade_level')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('miss_count')}
                    >
                      <div className="flex items-center">
                        Refusals
                        {getSortIcon('miss_count')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('current_consequence')}
                    >
                      <div className="flex items-center">
                        Consequence
                        {getSortIcon('current_consequence')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('makeup_days_owed')}
                    >
                      <div className="flex items-center">
                        Makeup Days
                        {getSortIcon('makeup_days_owed')}
                      </div>
                    </TableHead>
                    <TableHead>Assigned AP</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No consequence records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRecords.map((record) => (
                      <TableRow key={record.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{record.student_last_name || '-'}</TableCell>
                        <TableCell>{record.student_first_name || '-'}</TableCell>
                        <TableCell>{record.grade_level}</TableCell>
                        <TableCell>
                          <Badge className="bg-red-600 text-white font-bold">
                            {record.miss_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getConsequenceDisplay(record.current_consequence).color}>
                            {getConsequenceDisplay(record.current_consequence).label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={
                            (record.makeup_days_completed || 0) >= (record.makeup_days_owed || 0)
                              ? 'text-green-600 font-semibold'
                              : 'text-red-600 font-semibold'
                          }>
                            {record.makeup_days_completed || 0} / {record.makeup_days_owed || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          {record.assigned_ap_name ? (
                            <span className="text-sm">{record.assigned_ap_name}</span>
                          ) : (
                            isMainAdmin ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAssignAP(record.id, record.grade_level)}
                              >
                                Auto-Assign
                              </Button>
                            ) : (
                              <span className="text-slate-400 text-sm">Not assigned</span>
                            )
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            record.status === 'active' ? 'bg-orange-100 text-orange-800' :
                            record.status === 'resolved' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {record.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isMainAdmin ? (
                            <Button
                              size="sm"
                              onClick={() => handleViewDetails(record)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              View Details
                            </Button>
                          ) : (
                            <span className="text-slate-400 text-sm">View only</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {showDetailsModal && selectedStudent && isMainAdmin && (
          <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  {selectedStudent.student_first_name} {selectedStudent.student_last_name} - Consequence Details
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Student ID</p>
                    <p className="font-semibold">{selectedStudent.student_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Grade Level</p>
                    <p className="font-semibold">{selectedStudent.grade_level}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Total Refusals</p>
                    <Badge className="bg-red-600 text-white font-bold text-lg">
                      {selectedStudent.miss_count || 0}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Current Consequence</p>
                    <Badge className={getConsequenceDisplay(selectedStudent.current_consequence).color}>
                      {getConsequenceDisplay(selectedStudent.current_consequence).label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Assigned AP</p>
                    <p className="font-semibold">{selectedStudent.assigned_ap_name || 'Not assigned'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Status</p>
                    <Badge className={
                      selectedStudent.status === 'active' ? 'bg-orange-100 text-orange-800' :
                      selectedStudent.status === 'resolved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {selectedStudent.status}
                    </Badge>
                  </div>
                </div>

                <Card className="bg-red-50 border-red-200">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Refusal History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const missDates = parseArrayField(selectedStudent.miss_history);
                      if (missDates.length === 0) {
                        return <p className="text-sm text-slate-500">No refusal history</p>;
                      }
                      return (
                        <div className="flex flex-wrap gap-2">
                          {missDates.map((date, i) => (
                            <Badge key={i} variant="outline" className="border-red-300 text-red-700">
                              {safeFormatDate(date)}
                            </Badge>
                          ))}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Makeup Days Tracking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Makeup Days Owed:</span>
                      <Badge className="bg-orange-600 text-white">{selectedStudent.makeup_days_owed || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Makeup Days Completed:</span>
                      <Badge className="bg-green-600 text-white">{selectedStudent.makeup_days_completed || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Remaining:</span>
                      <Badge className={
                        (selectedStudent.makeup_days_owed || 0) - (selectedStudent.makeup_days_completed || 0) === 0
                          ? 'bg-green-600 text-white'
                          : 'bg-red-600 text-white'
                      }>
                        {(selectedStudent.makeup_days_owed || 0) - (selectedStudent.makeup_days_completed || 0)}
                      </Badge>
                    </div>
                    {(() => {
                      const makeupDates = parseArrayField(selectedStudent.makeup_dates);
                      if (makeupDates.length === 0) return null;
                      return (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs text-slate-600 mb-2">Completed makeup dates:</p>
                          <div className="flex flex-wrap gap-2">
                            {makeupDates.map((date, i) => (
                              <Badge key={i} variant="outline" className="border-green-300 text-green-700">
                                {safeFormatDate(date, 'MMM d')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                <Card className="bg-yellow-50 border-yellow-200">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Parent Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-700">Parent Contacted:</span>
                        <Badge className={selectedStudent.parent_contacted ? 'bg-green-600 text-white' : 'bg-slate-300 text-slate-700'}>
                          {selectedStudent.parent_contacted ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      {selectedStudent.parent_contact_date && (
                        <p className="text-xs text-slate-600">
                          Last contact: {safeFormatDate(selectedStudent.parent_contact_date)}
                        </p>
                      )}
                      {(() => {
                        const principalDates = parseArrayField(selectedStudent.principal_contact_history);
                        if (principalDates.length === 0) return null;
                        return (
                          <div className="mt-2 pt-2 border-t border-yellow-200">
                            <p className="text-xs text-slate-600 mb-2">Contact history:</p>
                            <div className="flex flex-wrap gap-2">
                              {principalDates.map((date, i) => (
                                <Badge key={i} variant="outline" className="border-yellow-300 text-yellow-700">
                                  {safeFormatDate(date, 'MMM d')}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Intervention Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(() => {
                      const notes = parseArrayField(selectedStudent.intervention_notes);
                      if (notes.length === 0) {
                        return <p className="text-sm text-slate-500">No notes added yet</p>;
                      }
                      return (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {[...notes].reverse().map((note, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-sm text-slate-700">{note?.note}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {safeFormatDate(note?.date)}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    <div className="space-y-2">
                      <Textarea
                        placeholder="Add intervention note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="h-24"
                      />
                      <Button
                        onClick={() => handleAddNote(selectedStudent.id)}
                        disabled={!newNote.trim() || addNoteMutation.isPending}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        Add Note
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <DialogFooter className="flex gap-2">
                  {selectedStudent.status === 'active' && (
                    <>
                      <Button
                        onClick={() => handleResolve(selectedStudent.id)}
                        className="bg-green-600 hover:bg-green-700"
                        disabled={resolveMutation.isPending}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark Resolved
                      </Button>
                      <Button
                        onClick={() => handleEscalate(selectedStudent.id)}
                        className="bg-red-700 hover:bg-red-800"
                        disabled={escalateMutation.isPending}
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Escalate to Principal
                      </Button>
                    </>
                  )}
                  {isMainAdmin && (
                    <Button
                      onClick={() => {
                        if (confirm(`⚠️ PERMANENTLY DELETE this record?\n\nStudent: ${selectedStudent.student_first_name} ${selectedStudent.student_last_name}\nThis action CANNOT be undone!\n\nClick OK to confirm deletion.`)) {
                          deleteRecordMutation.mutate(selectedStudent.id);
                        }
                      }}
                      variant="destructive"
                      disabled={deleteRecordMutation.isPending}
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Delete Record
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
